import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useHistory } from "react-router-dom";
import DateFnsUtils from "@date-io/date-fns";
import theme from "../../components/shared/theme";
import api from "../../store/api";
import {
  Grid,
  Checkbox,
  TextField,
  Button,
  FormControlLabel,
  Dialog,
  DialogContent,
  Select,
  DialogActions,
  MenuItem,
  Typography,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { ThemeProvider, makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import ClearIcon from "@material-ui/icons/Clear";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { RootState } from "../../store/rootReducer";
import { useSelector } from "react-redux";
import _ from "lodash";

const spinnerImage = require("../../assets/images/Spinner.gif");

interface WeekDate {
  name: string;
  date: string;
}

interface SelectItem extends React.Props<any> {
  text: string | undefined;
  value: string | undefined;
  selected: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    paper: {
      padding: theme.spacing(0),
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
    listItemTextPrimary: {
      fontSize: 46,
      fontWeight: 700,
    },
    listItemTextSecondary: {
      fontSize: 24,
      fontWeight: 700,
    },
    needsPoint: {
      fontSize: 19,
      fontWeight: 400,
    },
    badge: {
      borderRadius: 0,
    },
    submitButtonLabel: {
      textTransform: "capitalize",
      fontSize: 18,
      fontWeight: 400,
      color: "#FFF",
    },
    buttonRoot: {
      backgroundColor: "#F4B827",
      color: "#03090E",
      "&:hover": {
        backgroundColor: "#F4B827",
      },
      fontWeight: "bold",
    },
    formControlLabel: {
      fontSize: 18,
      color: "#4A4A4A",
      padding: theme.spacing(0, 0, 1, 0),
    },
    container: {
      backgroundColor: "#00152e",
      backgroundSize: "100%",
      borderRadius: "0",
      width: "100%",
      padding: 60,
    },
    headerContainer: {
      display: "flex",
    },
    button: {
      width: "100%",
    },
    fieldTitle: {
      marginBottom: 5,
      color: "#03090E",
    },

    textFieldInput: {
      backgroundColor: "#ECECEC",
      color: "black",
      height: 44,
      outlined: {
        fontSize: 100,
        transform: "translate(16px, 13px) scale(1)",
      },
    },
    dialogPaper: {
      minWidth: 500,
      margin: 0,
      borderRadius: 0,
      shadow: "none",
      backgroundColor: "#fff",
    },
    email: {
      fontFamily: "'Lato', sans-serif;",
      fontSize: 18,
      color: "#03090E",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 30,
    },
    modalTitle: {
      fontSize: 19,
      color: "#03090E",
    },
    estimatingDuration: {
      backgroundColor: "#333",
      borderRadius: 4,
      padding: 4,
      boxSizing: "border-box",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      marginTop: 10,
    },
    estimatedDuration: {
      backgroundColor: "#333",
      borderRadius: 4,
      padding: 12,
      boxSizing: "border-box",
      color: "#fff",
      marginTop: 10,
    },
    spinner: {
      height: 35,
    },
    MuiInputLabel: {
      color: "#03090E",
      outlined: {
        fontSize: 100,
        transform: "translate(16px, 13px) scale(1)",
      },
      shrink: {
        transform: "translate(16px, 13px) scale(1)",
      },
    },
    textFieldLabel: {
      color: "#03090E",
      transform: "translate(16px, 13px) scale(1)",

      shrink: {
        fontSize: 100,
      },
    },
    selectField: {
      height: 40,
    },
    helpText: {
      color: "red",
      padding: theme.spacing(0, 0, 0, 2),
    },
    dropdownIcon: {
      top: 4,
    },
    alertRoot: {
      margin: "10px 0",
    },
  })
);

const weekDay = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const calculateDate = () => {
  let _tempDate = new Date();
  let dateUtils = new DateFnsUtils();
  let dates: WeekDate[] = [];
  for (let date = 0; date <= 6; date++) {
    let datebody: WeekDate = {
      name: date === 0 ? "Today" : date === 1 ? "Tomorrow" : weekDay[_tempDate.getDay()],
      date: dateUtils.format(_tempDate, "yyyy-MM-dd"),
    };
    dates.push(datebody);
    _tempDate.setDate(_tempDate.getDate() + 1);
  }
  return dates;
};

const ScheduleTransferModal = (props: any) => {
  const classes = useStyles();
  const history = useHistory();
  const dateUtil = new DateFnsUtils();
  const {
    open,
    onClose: onCloseOuter,
    transferId,
    currentPriority,
    fromTransfer,
    locationId,
    scheduleDate,
    currentTransferData,
    showScheduleModalAssignVehicleOption,
    roles,
  } = props;
  const [priority, setPriority] = useState(Number(currentPriority) === 1 ? 1 : Number(currentPriority) === 2 ? 2 : 3);
  const [vehicleNo, setVehicleNo] = useState<String | null>(null);
  const [assignToMeChecked, setAssignToMeChecked] = useState<boolean>(false);
  const [vehicleNoList, setVehicleNoList] = useState<any[]>([]);
  const [days, setDays] = useState<WeekDate[]>(calculateDate());
  const { handleSubmit, watch, formState, getValues, setError, control, setValue, errors, trigger } = useForm();
  const [timeRange, setTimeRange] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [estimatorLoading, setEstimatorLoading] = useState(0);
  const [estimatedTripDuration, setEstimatedTripDuration] = useState("");
  const { authStore } = useSelector((state: RootState) => state);
  const { day: watchDay, mm: watchMM, hh: watchHH } = watch(["day", "mm", "hh"]);

  const loadVehicles = async () => {
    let vehicleNumbers: Number[] = [];
    if (authStore.data?.IsCustomer)
    {
      await api
      .getVehiclesBasedOnCustomer("", { locationId: authStore.data?.location||null })
      .then((res) => {
        vehicleNumbers = res.model.map((v: any) => {
          return String(v.text);
        });
      })
      .catch((error) => {
        setErrorMessage("Error while getting vehicles list, try to refresh the page");
      });
      setVehicleNoList(vehicleNumbers);
    }   
    else
    {     
      await api
      .getVehicles("", { fullNumber: true, smaUser: authStore.data?.IsSMAUser, locationId: locationId!==undefined ? locationId:null,transferId: transferId !==""?transferId:null })
      .then((res) => {
        vehicleNumbers = res.model.map((v: any) => {
          return String(v.text);
        });
      })
      .catch((error) => {});
    setVehicleNoList(vehicleNumbers);
  };
    }
   

  useEffect(() => {
    loadVehicles();
  }, []);

  useEffect(() => {
    if (open)
      loadVehicles();
  }, [open]);

  //useEffect(() => {
  //    if (scheduleDate) {
  //        setValue('hh', 22);
  //        setValue('mm', 22);
  //    }
  //}, [props]);

  const onClose = () => {
    setEstimatorLoading(0);
    onCloseOuter();
  };

  const estimateTripTime = async () => {
    // Old Implementation
    // let values = getValues();
    // let selectDate = dateUtil.parse(values.day, "yyyy-MM-dd");
    // let hours = values.hh;
    // let mins = values.mm;
    // let combinedate = concatTimeDate(selectDate, hours, mins);
    // setEstimatorLoading(1);
    // const _req = {
    //     startLocationLat: currentTransferData.originLatitude,
    //     startLocationLng: currentTransferData.originLongitude,
    //     endtLocationLat: currentTransferData.destinationLatitude,
    //     endtLocationLng: currentTransferData.destinationLongitude,
    //     numberOfClinicalStaff: 1,
    //     tasks: [
    //         {
    //             transferId,
    //             locationGooglePlaceId: currentTransferData.originGooglePlaceId,
    //             startedDateTime: combinedate,
    //             type: 1,
    //             ambulant: currentTransferData.mobility === 1 ? false : true,
    //         }
    //     ]
    // };
    // api.estimateTrip('', _req)
    // .then(res => {
    //     if(103) {
    //         const hours = 103 / 60;
    //         const minutes = res.tripDuration % 60;
    //         setEstimatedTripDuration(`${hours > 0 ? '' : `${hours} hour`} ${minutes} minutes`);
    //         setEstimatorLoading(2);
    //     }
    // }).catch(() => {
    //     setEstimatedTripDuration('Not available');
    //     setEstimatorLoading(2);
    // })

    const isValid = await trigger();
    if (!isValid) {
      setEstimatorLoading(0);
      return;
    }
    let values = getValues();
    let selectDate = dateUtil.parse(values.day, "yyyy-MM-dd");
    let hours = values.hh;
    let mins = values.mm;
    let combinedate = concatTimeDate(selectDate, hours, mins);
    setEstimatorLoading(1);
    const _req = {
      numberOfClinicalStaff: 1,
      transferId,
      startDateTime: combinedate,
    };
    api
      .calculateTransferDuration("", _req)
      .then((res) => {
        if (res.tripDuration) {
          const hours = Math.floor(res.tripDuration / 60);
          const minutes = res.tripDuration % 60;
          setEstimatedTripDuration(`${hours <= 0 ? "" : `${hours} hour${hours > 1 ? "s" : ""}`} ${minutes} minute${minutes > 1 ? "s" : ""}`);
          setEstimatorLoading(2);
        } else {
          setEstimatedTripDuration("Not available");
          setEstimatorLoading(2);
        }
      })
      .catch(() => {
        setEstimatedTripDuration("Not available");
        setEstimatorLoading(2);
      });
  };

  useEffect(() => {
    if (watchMM?.length === 2 && watchHH?.length === 2 && watchDay && watchDay?.length !== 0) {
      estimateTripTime();
    }
  }, [watchDay, watchMM, watchHH]);

  const getWorkingHoursOfLocations = async (locationId: any, date: any): Promise<void> => {
    let day = weekDay[new Date(date).getDay()];
    let _res = await api.getWorkingHoursOfLocations("locationId=" + locationId + "&weekDay=" + day);
    setTimeRange(_res === "" ? "" : _res);
  };

  const concatTimeDate = (date: Date, hh: string, mm: string) => {
    let concatDate = "";
    if (date?.getTime()) {
      concatDate = dateUtil.format(date, "yyyy-MM-dd") + "T" + ("0" + hh).slice(-2) + ":" + ("0" + mm).slice(-2);
      return concatDate;
    } else return undefined;
  };

  const initiateScheduleTransfer = async (): Promise<void> => {
    let data = await trigger();
    let values = getValues();
    let selectDate = dateUtil.parse(values.day, "yyyy-MM-dd");
    let hours = values.hh;
    let mins = values.mm;
    let combinedate = concatTimeDate(selectDate, hours, mins);

    let _req: { [index: string]: any } = {
      transferId: transferId,
      scheduledDate: combinedate,
      priority: priority,
      locationId:locationId
    };
    const vehicleNumber = roles.isSMADriver && assignToMeChecked ? authStore?.vehicleNumber : vehicleNo;

    if (!showScheduleModalAssignVehicleOption || _.isEmpty(vehicleNumber)) {
      await api
        .updateScheduleTransferDetails("", _req)
        .then((res) => {
          if (!res.hasError) {
            onClose();
          }
        })
        .catch((error) => {
          setErrorMessage(error.message);
        });
    } else {
      _req.vehicle = vehicleNumber;
      await api
        .scheduleAdHocTransfer("", _req)
        .then((res) => {
          if (res.hasError) {
            setErrorMessage(res.message);
          } else {
            onClose();
          }
        })
        .catch((error) => {
          setErrorMessage(error.message);
        });

      await api
        .getNotifyingUsers("vehicle=" + vehicleNumber)
        .then((res) => {})
        .catch((error) => {});
    }
  };

  const hh = React.createRef<HTMLInputElement>();
  const mm = React.createRef<HTMLInputElement>();

  return (
    <ThemeProvider theme={theme}>
      <Dialog classes={{ paper: classes.dialogPaper }} maxWidth="sm" open={open} onClose={onClose} aria-labelledby="form-dialog-title">
        <form onSubmit={handleSubmit(initiateScheduleTransfer)} autoComplete="off" id="scheduleForm">
          <DialogContent>
            <div className={classes.header}>
              <div className={classes.modalTitle}>Schedule Transfer</div>
              <ClearIcon onClick={onClose} style={{ cursor: "pointer" }} />
            </div>
            <Grid container spacing={1}>
              <Grid item xs={2}>
                <Controller
                  control={control}
                  defaultValue=""
                  render={(props) => (
                    <TextField
                      inputRef={hh}
                      value={props.value}
                      onChange={(e) => {
                        props.onChange(e);
                        if (e.target.value.length == 2) {
                          mm?.current?.focus();
                          return;
                        }
                      }}
                      InputProps={{ classes: { root: classes.textFieldInput } }}
                      InputLabelProps={{ classes: { root: classes.textFieldLabel } }}
                      variant="outlined"
                      placeholder="HH"
                      inputProps={{ maxLength: 2 }}
                    />
                  )}
                  name="hh"
                  rules={{
                    required: "Field is required",
                    validate: (value) => {
                      const hours = ("0" + new Date().getHours()).slice(-2);
                      const day = ("0" + new Date().getDate()).slice(-2);
                      if (getValues("day")?.length === 10) {
                        const date = getValues("day").split("-")[2];
                        if (day !== date) {
                          return true;
                        }
                      }
                      if (value.length != 2 || hours > value) {
                        return false;
                      }
                      return true;
                    },
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <Controller
                  control={control}
                  render={(props) => (
                    <TextField
                      inputRef={mm}
                      value={props.value}
                      onChange={(e) => {
                        props.onChange(e);
                        if (e.target.value.length == 0) {
                          hh?.current?.focus();
                          return;
                        }
                      }}
                      InputProps={{ classes: { root: classes.textFieldInput } }}
                      InputLabelProps={{ classes: { root: classes.textFieldLabel } }}
                      variant="outlined"
                      placeholder="MM"
                      inputProps={{ maxLength: 2 }}
                    />
                  )}
                  name="mm"
                  defaultValue=""
                  rules={{
                    required: "Field is required",
                    validate: (value) => {
                      const minutes = ("0" + new Date().getMinutes()).slice(-2);
                      const hours = ("0" + new Date().getHours()).slice(-2);
                      const day = ("0" + new Date().getDate()).slice(-2);
                      if (getValues("day")?.length === 10) {
                        const date = getValues("day").split("-")[2];
                        if (day !== date) {
                          return true;
                        }
                      }
                      if (value.length != 2 || (getValues("hh") == hours && minutes > value)) {
                        return false;
                      }
                      return true;
                    },
                  }}
                />
              </Grid>
              <Grid item xs={8}>
                <Controller
                  name="day"
                  defaultValue=""
                  control={control}
                  render={({ onChange, onBlur, value, name }) => (
                    <Select
                      onChange={(e) => {
                        onChange(e);
                      }}
                      value={value}
                      error={errors.day}
                      className={classes.selectField}
                      displayEmpty
                      IconComponent={KeyboardArrowDownIcon}
                      defaultValue=""
                      fullWidth={true}
                      variant="outlined"
                    >
                      <MenuItem key="None" value={""}>
                        Choose Day
                      </MenuItem>
                      {days.map((day: any) => {
                        return (
                          <MenuItem selected={day.selected} key={day.date} value={day.date}>
                            {day.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                  rules={{
                    required: "Day is required",
                  }}
                />
              </Grid>
            </Grid>
            <div style={{ margin: "10px 0px 10px 0px" }}>{timeRange}</div>

            {(errors.day || errors.mm || errors.hh) && (
              <div style={{ color: "crimson", marginBottom: 5 }}>Please select a valid date and time and not in the past.</div>
            )}

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <div className={classes.modalTitle}>Priority</div>
              </Grid>
              <Controller
                render={(props) => (
                  <RadioGroup style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }} aria-label="gender" name="priority">
                    <Grid item xs={2}>
                      <FormControlLabel
                        value="1"
                        checked={priority === 1 ? true : false}
                        onChange={(e) => {
                          setPriority(1);
                          props.onChange(e);
                        }}
                        control={<Radio color="primary" />}
                        label={
                          <div
                            style={{
                              height: 25,
                              width: 25,
                              borderRadius: 5,
                              backgroundColor: "#a30001",
                              color: "#fff",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            1
                          </div>
                        }
                        labelPlacement="end"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <FormControlLabel
                        value="2"
                        checked={priority === 2 ? true : false}
                        onChange={(e) => {
                          setPriority(2);
                          props.onChange(e);
                        }}
                        control={<Radio color="primary" />}
                        label={
                          <div
                            style={{
                              height: 25,
                              width: 25,
                              borderRadius: 5,
                              backgroundColor: "#c65200",
                              color: "#fff",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            2
                          </div>
                        }
                        labelPlacement="end"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <FormControlLabel
                        value="3"
                        checked={priority === 3 ? true : false}
                        onChange={(e) => {
                          setPriority(3);
                          props.onChange(e);
                        }}
                        control={<Radio color="primary" />}
                        label={
                          <div
                            style={{
                              height: 25,
                              width: 25,
                              borderRadius: 5,
                              backgroundColor: "#404549",
                              color: "#fff",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            3
                          </div>
                        }
                        labelPlacement="end"
                      />
                    </Grid>
                  </RadioGroup>
                )}
                name="priority"
                control={control}
                defaultValue=""
              />
            </Grid>

            {showScheduleModalAssignVehicleOption && !roles.isSMADriver && (
              <Grid container spacing={1} style={{ padding: "15px 0" }}>
                <Grid item xs={12}>
                  <div className={classes.modalTitle}>Assign to vehicle (optional)</div>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="vehicle"
                    defaultValue=""
                    control={control}
                    render={({ onChange, onBlur, value, name }) => (
                      <div>
                        <Select
                          key="Key"
                          fullWidth={true}
                          value={value}
                          displayEmpty
                          onChange={(e) => {
                            setVehicleNo(e.target.value + "");
                            onChange(e);
                          }}
                          IconComponent={KeyboardArrowDownIcon}
                          classes={{ icon: classes.dropdownIcon }}
                          variant="outlined"
                          defaultValue=""
                          error={errors.vehicle}
                        >
                          <MenuItem key="None" value={""}>
                            Choose Vehicle
                          </MenuItem>
                          {vehicleNoList.map((vehicleNo) => {
                            return (
                              <MenuItem key={vehicleNo} value={vehicleNo}>
                                {vehicleNo}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </div>
                    )}
                  />
                </Grid>
              </Grid>
            )}

            {showScheduleModalAssignVehicleOption && (roles.SMADriver || roles.CustomerDriver) && authStore?.vehicleNumber && (
              <Grid container spacing={1} style={{ padding: "15px 0" }}>
                <Grid item xs={12}>
                  <div className={classes.modalTitle}>Assign to vehicle</div>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="NoAllegies"
                    defaultValue=""
                    control={control}
                    render={(props) => (
                      <FormControlLabel
                        onChange={(e) => {
                          setAssignToMeChecked(!assignToMeChecked);
                          props.onChange(e);
                        }}
                        checked={assignToMeChecked}
                        control={<Checkbox color="primary" />}
                        label={<span style={{ fontWeight: 600 }}>Assign to me (vehicle {authStore?.vehicleNumber})</span>}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}

            <Grid container spacing={1}>
              <Grid item xs={12}>
                {estimatorLoading === 1 && (
                  <div className={classes.estimatingDuration}>
                    <img className={classes.spinner} src={spinnerImage} alt="spinner" />
                    <Typography>Estimating Transfer Duration</Typography>
                  </div>
                )}
                {estimatorLoading === 2 && (
                  <div className={classes.estimatedDuration}>
                    <Typography>Estimated Duration: {estimatedTripDuration}</Typography>
                    <Typography style={{ color: "#ffffffa0", fontSize: 16 }}>
                      Please ensure this transfer doesn't overlap with other scheduled transfers.
                    </Typography>
                  </div>
                )}
              </Grid>
            </Grid>

            {errorMessage !== "" && (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Alert variant="filled" severity="error" classes={{ root: classes.alertRoot }}>
                    {errorMessage}
                  </Alert>
                </Grid>
              </Grid>
            )}
          </DialogContent>

          <DialogActions>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Button
                  className={classes.button}
                  onClick={onClose}
                  style={{ fontWeight: "bold", backgroundColor: "#ECECEC", color: "#090F15" }}
                  color="primary"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button className={classes.button} disabled={formState.isSubmitting} type="submit" style={{ fontWeight: "bold" }} color="primary">
                  Schedule
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
};

export default ScheduleTransferModal;
